import { Component, EventEmitter, Output } from '@angular/core';
import { forkJoin } from 'rxjs';

import { DocumentApiService } from 'src/app/services/document-api.service';
import { ManufacturingBackdocRecompute, ManufacturingBackdocRecomputeBreakdownRow } from 'src/app/shared/manufacturing-backdoc-recompute';
import { round2 } from 'src/app/shared/round';

/**
 * Read-only popup showing whether a manufacturing backward-leg document's
 * stored price still matches what its actual forward legs say it should be
 * — the DB-side recompute behind
 * `docs/PROMPT_manufacturing-backdoc-price-check-dialog.md`. Wraps
 * `GET /Document/:id/ManufacturingRecompute` (summary) and
 * `.../ManufacturingRecomputeBreakdown` (per-forward-leg detail), both
 * backed by `SQL-Database-migration/FUNCTIONS/
 * ufn_getmanufacturingbackdoc_recompute_iTVF_deploy.sql` and its
 * `_breakdown_iTVF` sibling.
 *
 * Same "self-contained informational popup" architecture as
 * `IncomePriceBreakdownDialogComponent` (local `visible` boolean,
 * `p-dialog [(visible)]`, opened imperatively via `open()` through a
 * `@ViewChild` reference from `edit-document-content-receipt`) — deliberately
 * NOT the parent-controlled `@Input() display` pattern full create/edit
 * forms use, since this dialog is a single reused instance opened per-row,
 * on demand (click-triggered), not tied to any one row's own lifecycle.
 *
 * **Does not write anything itself.** `DOCITEMPRICE`/`DOCITEMVPRICE` are
 * ordinary editable fields on the host document, already backed by
 * `edit-document-content-receipt.component.ts`'s own
 * `onDOCITEMVPRICEChange`/`onDOCITEMPRICEChange` handlers and the normal
 * document save flow. This dialog only emits `applyCorrection` with the
 * confirmed recomputed values when the user clicks the button — the host
 * component owns which `DocItems` row to apply them to and how (see
 * `EditDocumentContentReceiptComponent.applyManufacturingBackdocCorrection`).
 * The button is disabled whenever `IsFullyConfirmed` is false, full stop —
 * a BOM/document/parts discrepancy must never be papered over by a price
 * write, and with no dedicated write endpoint this is the only gate there
 * is, so it is never bypassed.
 */
@Component({
  selector: 'app-manufacturing-backdoc-price-check-dialog',
  templateUrl: './manufacturing-backdoc-price-check-dialog.component.html',
  styleUrls: ['./manufacturing-backdoc-price-check-dialog.component.scss'],
})
export class ManufacturingBackdocPriceCheckDialogComponent {

  /** Same threshold as `IncomePriceBreakdownDialogComponent` and the SQL-side
   *  report/backfill's `@FloatTol` default — kept identical across all three
   *  so "material" means the same thing everywhere this feature appears. */
  private readonly MISMATCH_TOLERANCE = 0.5;

  visible = false;
  loading = false;
  error = false;

  summary: ManufacturingBackdocRecompute | null = null;
  breakdownRows: ManufacturingBackdocRecomputeBreakdownRow[] = [];

  /** Emitted when the user clicks "Apply correction" — always carries a
   *  fully-confirmed `summary` (the button is disabled otherwise). The host
   *  is responsible for writing `RecomputedUnitPriceCurrency`/
   *  `RecomputedUnitPriceMain` onto whichever `DocItems` row it opened this
   *  dialog for; this component has no reference to that row at all. */
  @Output() applyCorrection = new EventEmitter<ManufacturingBackdocRecompute>();

  constructor(private docApi: DocumentApiService) { }

  /**
   * @param backDocId - Backward-leg document ID (`DOCID`, DOCTYPE=5).
   * @param timeWindowMinutes - Matching time window in minutes (defaults to
   *   15, matching the SQL functions' own default).
   */
  open(backDocId: number, timeWindowMinutes: number = 15): void {
    this.visible = true;
    this.loading = true;
    this.error = false;
    this.summary = null;
    this.breakdownRows = [];

    forkJoin({
      summary: this.docApi.getManufacturingBackdocRecompute(backDocId, timeWindowMinutes),
      breakdown: this.docApi.getManufacturingBackdocRecomputeBreakdown(backDocId, timeWindowMinutes),
    }).subscribe({
      next: ({ summary, breakdown }) => {
        this.summary = summary?.[0] ?? null;
        this.breakdownRows = breakdown ?? [];
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });
  }

  onHide(): void {
    this.visible = false;
  }

  onApplyClick(): void {
    if (!this.summary || !this.canApply) {
      return;
    }
    this.applyCorrection.emit(this.summary);
    this.onHide();
  }

  /** `true` when the stored vs. recomputed main-currency price differs by
   *  more than {@link MISMATCH_TOLERANCE} — the actionable case this dialog
   *  exists to surface. `IsFullyConfirmed=true` with `isMismatch=false` means
   *  the stored price is already correct, nothing to do. */
  get isMismatch(): boolean {
    if (!this.summary) {
      return false;
    }
    return Math.abs(this.summary.RecomputedUnitPriceMain - this.summary.StoredUnitPriceMain) > this.MISMATCH_TOLERANCE;
  }

  /** Gate for the "Apply correction" button — `IsFullyConfirmed` must be
   *  true (never apply an unconfirmed match) AND the delta must be material
   *  (nothing to apply on an already-correct price). This is the ONLY
   *  guard standing between a user and a bad write, since there is no
   *  server-side write endpoint to re-check it — see this class's own doc
   *  comment. */
  get canApply(): boolean {
    return !!this.summary?.IsFullyConfirmed && this.isMismatch;
  }

  /** Plain-language reason the match isn't (fully) confirmed, mirroring
   *  `usp_ManufacturingBackdocPriceDiscrepancyReport`'s own
   *  `ConfirmationStatus` CASE expression — same wording family, so the
   *  message here agrees with what the SQL-side report would say about the
   *  same document. `null` when there is nothing to report (fully
   *  confirmed, or no data loaded yet). */
  get unconfirmedReason(): string | null {
    if (!this.summary || this.summary.IsFullyConfirmed) {
      return null;
    }
    if (this.summary.MatchedForwardDocCount === 0) {
      return 'Не знайдено відповідних документів передачі на виробництво';
    }
    if (this.summary.PrimDocIdMatchedCount !== this.summary.MatchedForwardDocCount) {
      return 'Розбіжність PRIMDOCID хоча б для одного зі знайдених документів';
    }
    if (this.summary.BOMMismatchedPartCount > 0) {
      return 'Розбіжність кількості за специфікацією (BOM)';
    }
    return null;
  }

  get totalContributionCurrency(): number {
    return round2(this.breakdownRows.reduce((sum, r) => sum + (r.ContributionCurrency ?? 0), 0));
  }
}
